import { useRef, useCallback, useEffect, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

interface EditableFieldProps {
  value: string;
  onChange: (value: string) => void;
  /** Called with the field's value when it loses focus — for commit-time validation (e.g. restore-on-blank, #5981). */
  onBlur?: (value: string) => void;
  onDelete?: () => void;
  /** Accessible label for the delete button (icon-only). Defaults to 'Delete'. */
  deleteLabel?: string;
  maxLength?: number;
  placeholder?: string;
  hideBullet?: boolean;
  disabled?: boolean;
  className?: string;
  /** Short message ('Required') rendered red in the counter slot, replacing the counter while set (#5981). */
  errorMessage?: string | null;
  /** Show the input border at all times instead of the default hover-to-reveal styling (#6177). */
  alwaysBordered?: boolean;
}

const HOVER_CLASSES = 'bg-transparent border-transparent shadow-none group-hover:bg-white group-hover:border-input group-hover:shadow-sm focus:bg-white focus:border-input focus:shadow-sm [@media(hover:none)]:bg-white [@media(hover:none)]:border-input [@media(hover:none)]:shadow-sm text-sm';

export function EditableField({
  value,
  onChange,
  onBlur,
  onDelete,
  deleteLabel = 'Delete',
  maxLength,
  placeholder,
  hideBullet = false,
  disabled = false,
  className,
  errorMessage,
  alwaysBordered = false,
}: EditableFieldProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [localValue, setLocalValue] = useState(value);
  const isFocusedRef = useRef(false);

  // Sync from prop when it changes externally (e.g. after API refetch)
  // Skip sync while the user is actively typing (field has focus)
  useEffect(() => {
    if (!isFocusedRef.current) {
      setLocalValue(value);
    }
  }, [value]);

  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = textarea.scrollHeight + 'px';
    }
  }, []);

  useEffect(() => {
    adjustHeight();
  }, [localValue, adjustHeight]);

  useEffect(() => {
    if (!textareaRef.current) return;
    const container = textareaRef.current.parentElement;
    if (!container) return;
    const observer = new ResizeObserver(() => adjustHeight());
    observer.observe(container);
    return () => observer.disconnect();
  }, [adjustHeight]);

  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const handleChange = (newValue: string) => {
    if (!maxLength || newValue.length <= maxLength || newValue.length < localValue.length) {
      setLocalValue(newValue);
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        onChangeRef.current(newValue);
      }, 300);
    }
  };

  const handleFocus = () => {
    isFocusedRef.current = true;
  };

  const handleBlur = () => {
    isFocusedRef.current = false;
    clearTimeout(debounceRef.current);
    if (localValue !== value) {
      onChange(localValue);
    }
    onBlur?.(localValue);
  };

  useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);

  const isOverLimit = maxLength ? localValue.length > maxLength : false;

  return (
    <div className={cn('flex items-start gap-1 group', className)}>
      {!hideBullet && <span className='text-gray-300 text-sm ml-1 mt-1.5'>●</span>}
      <div className='flex-1 relative'>
        <Textarea
          ref={textareaRef}
          value={localValue}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          rows={1}
          placeholder={placeholder}
          className={cn(
            alwaysBordered ? 'text-sm' : HOVER_CLASSES,
            'min-h-0 resize-none overflow-hidden px-2 py-1.5',
            maxLength && 'pr-14',
            isOverLimit && 'border-red-300 focus:border-red-500',
          )}
        />
        {(maxLength || errorMessage) && (
          <span className={cn(
            'absolute right-2 top-2 text-xs',
            errorMessage || isOverLimit ? 'text-red-500' : 'text-gray-400',
          )}>
            {errorMessage ?? `${localValue.length}/${maxLength}`}
          </span>
        )}
      </div>
      {onDelete && !disabled && (
        <button
          type='button'
          aria-label={deleteLabel}
          className='p-1 mt-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors cursor-pointer'
          onClick={onDelete}
        >
          <Trash2 className='h-4 w-4' aria-hidden='true' />
        </button>
      )}
    </div>
  );
}
